'use client';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useId, useRef, useState, } from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { cx } from '../../../internal/cx.js';
import { ChevronDownGlyph } from '../../../internal/icons.js';
import { useFieldContext, useFieldControl } from '../form-field/index.js';
import { Combobox, } from '../combobox/index.js';
/**
 * Pick a record (a project, contact, company, team…) from a searchable list in
 * a popover. The product supplies the records, search, rendering and creation;
 * EntityPicker supplies the trigger, the popover, keyboard support and the
 * single or multiple selection model. Built on Combobox (`inline`).
 */
export const EntityPicker = forwardRef(function EntityPicker({ options = [], onSearch, searchDebounce, minSearchLength, value: valueProp, defaultValue = null, onValueChange, multiple = false, values: valuesProp, defaultValues, onValuesChange, valueOptions, onCreate, clearOption, renderOption, filter, sortByMatch = true, limit, loading, emptyMessage, placeholder = 'Select…', searchPlaceholder = 'Search…', searchLabel, footer, trigger, renderValue, open: openProp, defaultOpen = false, onOpenChange, align = 'start', size = 'md', invalid, closeOnSelect, contentClassName, messages, className, ...rest }, ref) {
    const id = useId();
    const valueId = `mn-entity-picker-${id}-value`;
    const searchId = `mn-entity-picker-${id}-search`;
    const panelId = `mn-entity-picker-${id}-panel`;
    const field = useFieldContext();
    // A button takes no `required` attribute; `aria-required` from the Field stays.
    const { required: _required, ...fieldProps } = useFieldControl(rest, { invalid });
    const triggerId = fieldProps.id ?? `mn-entity-picker-${id}-trigger`;
    const shouldClose = closeOnSelect ?? !multiple;
    const [openState, setOpenState] = useState(defaultOpen);
    const open = openProp ?? openState;
    const setOpen = useCallback((next) => {
        setOpenState(next);
        onOpenChange?.(next);
    }, [onOpenChange]);
    const [internalValue, setInternalValue] = useState(defaultValue);
    const [internalValues, setInternalValues] = useState(defaultValues ?? []);
    const value = valueProp !== undefined ? valueProp : internalValue;
    const values = valuesProp ?? internalValues;
    const [query, setQuery] = useState('');
    // Labels for the trigger: every record seen in options, value options, search results or creations.
    const known = useRef(new Map());
    for (const o of valueOptions ?? [])
        known.current.set(o.value, o);
    for (const o of options)
        known.current.set(o.value, o);
    const optionFor = (v) => options.find((o) => o.value === v) ?? known.current.get(v) ?? { value: v, label: v };
    const selectedValues = multiple ? values : value != null ? [value] : [];
    const selected = selectedValues.map(optionFor);
    const search = onSearch
        ? async (q, ctx) => {
            const found = await onSearch(q, ctx);
            for (const o of found)
                known.current.set(o.value, o);
            return found;
        }
        : undefined;
    const create = onCreate
        ? async (q) => {
            const made = await onCreate(q);
            if (made)
                known.current.set(made.value, made);
            return made;
        }
        : undefined;
    const close = () => setOpen(false);
    let summary;
    if (selected.length === 0)
        summary = placeholder;
    else if (renderValue)
        summary = renderValue(selected);
    else if (selected.length === 1 && selected[0]) {
        const only = selected[0];
        summary = (_jsxs(_Fragment, { children: [only.media ? (_jsx("span", { className: "mn-entity-picker__media", "aria-hidden": true, children: only.media })) : null, _jsx("span", { className: "mn-entity-picker__label", children: only.label })] }));
    }
    else {
        summary = (_jsxs(_Fragment, { children: [_jsx("span", { className: "mn-entity-picker__label", children: selected[0]?.label }), _jsxs("span", { className: "mn-entity-picker__more", children: ["+", selected.length - 1] })] }));
    }
    const ariaLabel = rest['aria-label'];
    // A select-only combobox (APG): named by the label, its value is the visible content.
    const labelledBy = rest['aria-labelledby'] ?? (field && !ariaLabel ? field.labelId : undefined);
    const dialogName = ariaLabel ?? (field ? undefined : placeholder);
    const defaultTrigger = (_jsxs("button", { type: "button", role: "combobox", "aria-expanded": open, "aria-controls": open ? panelId : undefined, ...fieldProps, id: triggerId, "aria-labelledby": labelledBy, className: cx('mn-input', 'mn-entity-picker', className), "data-size": size, "data-placeholder": selected.length === 0 || undefined, "data-invalid": fieldProps['aria-invalid'] ? true : undefined, "data-disabled": fieldProps.disabled || undefined, onKeyDown: (e) => {
            fieldProps.onKeyDown?.(e);
            // APG select-only combobox: the arrow keys open the list too.
            if (!e.defaultPrevented && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
                e.preventDefault();
                setOpen(true);
            }
        }, children: [_jsx("span", { id: valueId, className: "mn-entity-picker__value", children: summary }), _jsx(ChevronDownGlyph, { className: "mn-entity-picker__icon" })] }));
    return (_jsxs(PopoverPrimitive.Root, { open: open, onOpenChange: (next) => {
            setOpen(next);
            if (!next)
                setQuery('');
        }, children: [_jsx(PopoverPrimitive.Trigger, { asChild: true, ref: ref, children: trigger ?? defaultTrigger }), _jsx(PopoverPrimitive.Portal, { children: _jsxs(PopoverPrimitive.Content, { id: panelId, align: align, sideOffset: 4, "aria-label": dialogName, "aria-labelledby": dialogName ? undefined : field?.labelId, className: cx('mn-menu', 'mn-entity-picker__content', contentClassName), children: [_jsx(Combobox, { inline: true, id: searchId, invalid: false, required: false, "aria-label": searchLabel ?? searchPlaceholder, listLabel: dialogName ?? undefined, placeholder: searchPlaceholder, size: size === 'lg' ? 'md' : size, options: options, onSearch: search, searchDebounce: searchDebounce, minSearchLength: minSearchLength, multiple: multiple, value: multiple ? undefined : value, values: multiple ? values : undefined, valueOptions: selected, onValueChange: (next) => {
                                if (valueProp === undefined)
                                    setInternalValue(next);
                                onValueChange?.(next);
                                if (shouldClose)
                                    close();
                            }, onValuesChange: (next) => {
                                if (valuesProp === undefined)
                                    setInternalValues(next);
                                onValuesChange?.(next);
                                if (shouldClose)
                                    close();
                            }, onInputValueChange: setQuery, onCreate: create, clearOption: clearOption, renderOption: renderOption, filter: filter, sortByMatch: sortByMatch, limit: limit, loading: loading, emptyMessage: emptyMessage, messages: messages }), footer ? (_jsx("div", { className: "mn-entity-picker__footer", children: typeof footer === 'function' ? footer({ close, query }) : footer })) : null] }) })] }));
});
//# sourceMappingURL=entity-picker.js.map